import { useState, useEffect, useCallback } from "react";
import { Upload, FileText, Users, Clock, CheckCircle, AlertCircle, Search } from "lucide-react";
import api from "../services/api";
import ContactUpload from "../components/ContactUpload";
import UploadProgress from "../components/UploadProgress";

const statusBadge = {
  pending: { color: "bg-gray-100 text-gray-700", label: "Pending" },
  processing: { color: "bg-blue-100 text-blue-700", label: "Processing" },
  completed: { color: "bg-green-100 text-green-700", label: "Completed" },
  failed: { color: "bg-red-100 text-red-700", label: "Failed" },
};

export default function ContactLists() {
  const [uploads, setUploads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [activeUpload, setActiveUpload] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("uploads"); // uploads, contacts
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchUploads = useCallback(async () => {
    try {
      const res = await api.get("/contacts/uploads", { params: { page } });
      setUploads(res.data.uploads);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch uploads:", err);
    }
  }, [page]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await api.get("/contacts", { params: { page, search, per_page: 25 } });
      setContacts(res.data.contacts);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch contacts:", err);
    }
  }, [page, search]);

  useEffect(() => {
    setLoading(true);
    if (tab === "uploads") {
      fetchUploads().finally(() => setLoading(false));
    } else {
      fetchContacts().finally(() => setLoading(false));
    }
  }, [tab, fetchUploads, fetchContacts]);

  const handleUploadStarted = (contactList) => {
    setActiveUpload(contactList);
    setShowUpload(false);
    fetchUploads();
  };

  const handleUploadComplete = () => {
    setActiveUpload(null);
    fetchUploads();
    fetchContacts();
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Contacts</h1>
          <p className="text-sm text-gray-500 mt-1">Upload and manage your contact lists</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Upload className="w-4 h-4" />
          Upload CSV
        </button>
      </div>

      {/* Active upload progress */}
      {activeUpload && (
        <div className="mb-6">
          <UploadProgress contactList={activeUpload} onComplete={handleUploadComplete} />
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="mb-6">
          <ContactUpload onUploadStarted={handleUploadStarted} onClose={() => setShowUpload(false)} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        <button
          onClick={() => { setTab("uploads"); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "uploads" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <FileText className="w-4 h-4 inline mr-1.5" />
          Upload History
        </button>
        <button
          onClick={() => { setTab("contacts"); setPage(1); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            tab === "contacts" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Users className="w-4 h-4 inline mr-1.5" />
          All Contacts
        </button>
      </div>

      {/* Search (contacts tab only) */}
      {tab === "contacts" && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or phone number..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {/* Upload History Tab */}
      {tab === "uploads" && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">File</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Valid</th>
                <th className="px-4 py-3 text-right">Dups</th>
                <th className="px-4 py-3 text-right">Opt-outs</th>
                <th className="px-4 py-3 text-right">Errors</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uploads.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No uploads yet. Click "Upload CSV" to get started.
                  </td>
                </tr>
              ) : (
                uploads.map((u) => {
                  const badge = statusBadge[u.status] || statusBadge.pending;
                  return (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-800 font-medium">{u.name}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">{u.row_count}</td>
                      <td className="px-4 py-3 text-sm text-green-600 text-right font-medium">{u.valid_count}</td>
                      <td className="px-4 py-3 text-sm text-amber-600 text-right">{u.duplicate_count}</td>
                      <td className="px-4 py-3 text-sm text-red-600 text-right">{u.opt_out_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 text-right">{u.error_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Contacts Tab */}
      {tab === "contacts" && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Custom Fields</th>
                <th className="px-4 py-3 text-left">Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">
                    {search ? "No contacts match your search." : "No contacts yet."}
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">{c.full_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{c.phone_number}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.custom_fields && Object.keys(c.custom_fields).length > 0
                        ? Object.entries(c.custom_fields).slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(", ")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {new Date(c.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 text-sm text-gray-600 border rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 text-sm text-gray-600 border rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
